import {
  area,
  bbox,
  center as findCenter,
  featureCollection as FeatureCollection,
  point as Point,
} from "@turf/turf"

const MAX_MAP_ZOOM = 15.5

export const getCenterOfFeature = feature => {
  if (!feature) {
    return null
  }

  const centerPoint = findCenter(FeatureCollection([feature]))
  return centerPoint.geometry.coordinates
}

export const centerMapOnFeatures = (map, center) => {
  if (!map || !center) {
    return false
  }

  map.flyTo({ center })
  return true
}

const allFinite = values => values.every(val => Number.isFinite(val))

const isValidFlatBounds = bounds =>
  bounds.length === 4 &&
  bounds.every(val => typeof val === "number") &&
  allFinite(bounds)

const isValidNestedBounds = bounds => {
  if (bounds.length !== 2) return false
  const [sw, ne] = bounds
  if (!Array.isArray(sw) || !Array.isArray(ne)) return false
  if (sw.length !== 2 || ne.length !== 2) return false
  return allFinite(sw) && allFinite(ne)
}

/**
 * Validates that bounds array contains valid finite numbers.
 * Mapbox fitBounds accepts two formats:
 * 1. Flat array (turf bbox): [west, south, east, north] (4 numbers)
 * 2. Nested array: [[lng1, lat1], [lng2, lat2]] (2 arrays of 2 numbers each)
 * @param {Array} bounds - Bounds in either format
 * @returns {boolean} True if bounds are valid
 */
export const isValidBounds = bounds => {
  if (!bounds || !Array.isArray(bounds)) return false
  return isValidFlatBounds(bounds) || isValidNestedBounds(bounds)
}

export const fitToBounds = (map, bbox, options) => {
  if (!map || !bbox) {
    return
  }

  // Validate bounds contain valid numbers to prevent NaN crashes
  if (!isValidBounds(bbox)) {
    console.warn("Invalid bounds detected, skipping fitToBounds:", bbox)
    return
  }

  if (!options) {
    options = {}
  }

  if (options.maxZoom == null) {
    options.maxZoom = MAX_MAP_ZOOM
  }

  map.fitBounds(bbox, options)
}

export const getSmallestFeature = features => {
  const points = []
  const polygons = []

  for (const feature of features) {
    if (feature.geometry.type === "Point") {
      points.push(feature)
      continue
    }

    if (feature.geometry.type === "Polygon") {
      polygons.push(feature)
    }
  }

  const point = points.reduce((accumulator, value) => {
    if (accumulator?.feature?.floor == null) {
      return value
    }

    return accumulator.feature.floor < value.feature.floor ? accumulator : value
  }, null)

  const polygon = polygons
    .map(f => ({ feature: f, size: area(f) }))
    .reduce((accumulator, value) => {
      if (accumulator?.feature == null) {
        return value
      }

      return accumulator.size < value.size ? accumulator : value
    }, null)

  return { point, polygon: polygon?.feature }
}

export const PolygonPointOption = {
  TOP_LEFT: 0,
  TOP_CENTER: 1,
  TOP_RIGHT: 2,
  RIGHT: 3,
  BOTTOM_RIGHT: 4,
  BOTTOM_CENTER: 5,
  BOTTOM_LEFT: 6,
  LEFT: 7,
  CENTER: 8,
}

const polygonPointCoords = {
  [PolygonPointOption.TOP_LEFT]: (minLng, maxLng, minLat, maxLat) =>
    [minLng, maxLat],
  [PolygonPointOption.TOP_CENTER]: (minLng, maxLng, minLat, maxLat) =>
    [minLng + (maxLng - minLng) / 2, maxLat],
  [PolygonPointOption.TOP_RIGHT]: (minLng, maxLng, minLat, maxLat) =>
    [maxLng, maxLat],
  [PolygonPointOption.RIGHT]: (minLng, maxLng, minLat, maxLat) =>
    [maxLng, minLat + (maxLat - minLat) / 2],
  [PolygonPointOption.BOTTOM_RIGHT]: (minLng, maxLng, minLat, maxLat) =>
    [maxLng, minLat],
  [PolygonPointOption.BOTTOM_CENTER]: (minLng, maxLng, minLat, maxLat) =>
    [minLng + (maxLng - minLng) / 2, minLat],
  [PolygonPointOption.BOTTOM_LEFT]: (minLng, maxLng, minLat, maxLat) =>
    [minLng, minLat],
  [PolygonPointOption.LEFT]: (minLng, maxLng, minLat, maxLat) =>
    [minLng, minLat + (maxLat - minLat) / 2],
  [PolygonPointOption.CENTER]: (minLng, maxLng, minLat, maxLat) =>
    [minLng + (maxLng - minLng) / 2, minLat + (maxLat - minLat) / 2],
}

export const getPointOnPolygon = (polygon, option = PolygonPointOption.TOP_CENTER) => {
  const boundingBox = bbox(polygon)
  if (!boundingBox) {
    return null
  }

  const minLng = Math.min(boundingBox[0], boundingBox[2])
  const maxLng = Math.max(boundingBox[0], boundingBox[2])
  const minLat = Math.min(boundingBox[1], boundingBox[3])
  const maxLat = Math.max(boundingBox[1], boundingBox[3])

  const coordsFn = polygonPointCoords[option]
  if (!coordsFn) {
    throw new Error(`Unsupported option: ${option}`)
  }

  return new Point(coordsFn(minLng, maxLng, minLat, maxLat))
}

/**
 * Pan the map to the center of the given features while preserving zoom level.
 * Uses flyTo for smooth animation instead of fitBounds which resets zoom.
 *
 * @param {mapboxgl.Map} map - The Mapbox GL map instance
 * @param {GeoJSON.FeatureCollection|GeoJSON.Feature} features - Features to center on
 * @param {Object} options - Optional flyTo options (duration, padding, etc.)
 */
export const panToZoneCenter = (map, features, options = {}) => {
  if (!map || !features) {
    return
  }

  let centerPoint
  try {
    centerPoint = findCenter(features)
  } catch (error) {
    console.warn("Could not calculate center for features:", error.message)
    return
  }

  if (!centerPoint?.geometry?.coordinates) {
    console.warn("Could not calculate center for features, skipping pan")
    return
  }

  const currentZoom = map.getZoom()
  const [lng, lat] = centerPoint.geometry.coordinates

  map.flyTo({
    center: [lng, lat],
    zoom: currentZoom,
    duration: 500,
    ...options,
  })
}

/**
 * Danger zone threshold in CSS pixels from viewport edges.
 * Shared between calculateScreenAnchor (flow tooltips) and
 * DANGER_ZONE_OFFSETS in default.js (polygon tooltips).
 * Exported so both systems reference one source of truth.
 */
export const SCREEN_ANCHOR_THRESHOLD = 100

/** Determines which viewport edges the cursor is near, accounting for padding and threshold overrides. */
const computeEdgeFlags = (point, canvasWidth, canvasHeight, padding, options) => {
  const pad = {
    top: padding.top || 0,
    bottom: padding.bottom || 0,
    left: padding.left || 0,
    right: padding.right || 0,
  }

  const topThreshold = options.topThreshold ?? SCREEN_ANCHOR_THRESHOLD
  const bottomThreshold = options.bottomThreshold ?? SCREEN_ANCHOR_THRESHOLD

  return {
    nearTop: point.y < pad.top + topThreshold,
    nearBottom: point.y > canvasHeight - pad.bottom - bottomThreshold,
    nearLeft: point.x < pad.left + SCREEN_ANCHOR_THRESHOLD,
    nearRight: point.x > canvasWidth - pad.right - SCREEN_ANCHOR_THRESHOLD,
  }
}

/** Converts edge proximity flags into a Mapbox popup anchor string (e.g., "top-left", "bottom"). */
const resolveAnchor = ({ nearTop, nearBottom, nearLeft, nearRight }) => {
  const vertical = nearTop ? "top" : nearBottom ? "bottom" : ""
  const horizontal = nearLeft ? "left" : nearRight ? "right" : ""
  if (vertical && horizontal) return `${vertical}-${horizontal}`
  return vertical || horizontal || "bottom"
}

/**
 * Calculates the optimal Mapbox popup anchor based on cursor screen position
 * relative to viewport edges. Used for cursor-following popups (e.g., flow tooltips)
 * where the popup position tracks the mouse and needs to flip when near edges.
 *
 * IMPORTANT: All dimensions must be in CSS pixels (not device/render-buffer pixels).
 * Use canvas.clientWidth/clientHeight (not canvas.width/height) to avoid HiDPI mismatch
 * since e.point reports CSS pixel coordinates.
 *
 * @param {Object} point - CSS pixel coordinates {x, y} from map event e.point
 * @param {number} canvasWidth - Map canvas CSS width (canvas.clientWidth)
 * @param {number} canvasHeight - Map canvas CSS height (canvas.clientHeight)
 * @param {Object} [padding] - Map padding {top, bottom, left, right} in CSS pixels
 * @param {Object} [options] - Additional options
 * @param {number} [options.topThreshold] - Override top-edge threshold (CSS pixels).
 *   Use for tall popups (e.g., flow tooltips with 300px max-height) that need to flip
 *   anchor further from the top edge than the default SCREEN_ANCHOR_THRESHOLD (ENG-5462).
 * @param {number} [options.bottomThreshold] - Override bottom-edge threshold (CSS pixels).
 * @returns {string} Mapbox popup anchor string (e.g., "bottom", "top-left")
 */
export function calculateScreenAnchor(point, canvasWidth, canvasHeight, padding = {}, options = {}) {
  const flags = computeEdgeFlags(point, canvasWidth, canvasHeight, padding, options)
  return resolveAnchor(flags)
}
